import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Routes, Route, Navigate } from 'react-router-dom';
import { api } from '@/lib/api.ts';
import { Login } from '@/routes/Login.tsx';
import { Layout } from '@/components/Layout.tsx';
import { Today, Upcoming, Inbox, AllTasks, ProjectView } from '@/routes/Views.tsx';
import { TableView } from '@/routes/TableView.tsx';
import { BoardView } from '@/routes/BoardView.tsx';
import { Search } from '@/routes/Search.tsx';
import { Review } from '@/routes/Review.tsx';
import { CommandPalette } from '@/components/CommandPalette.tsx';

const AUTH_KEY = ['auth'] as const;

export function App() {
  const queryClient = useQueryClient();

  const authQuery = useQuery({
    queryKey: AUTH_KEY,
    queryFn: () => api.auth.me(),
    // Auth state decides what the whole app renders — always fresh.
    staleTime: 0,
  });

  if (authQuery.isPending) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <span className="text-sm" style={{ color: 'var(--color-ink-2)' }}>
          Loading…
        </span>
      </div>
    );
  }

  if (authQuery.isError) {
    return (
      <div className="flex min-h-full items-center justify-center px-6 text-center">
        <div>
          <p className="text-sm" style={{ color: 'var(--color-brick)' }}>
            Cannot reach TDL.
          </p>
          <p className="mt-1 text-sm" style={{ color: 'var(--color-ink-2)' }}>
            Check that the computer running it is switched on.
          </p>
        </div>
      </div>
    );
  }

  const auth = authQuery.data;

  if (!auth.authenticated) {
    return (
      <Login
        configured={auth.configured}
        onAuthenticated={() => void queryClient.invalidateQueries({ queryKey: AUTH_KEY })}
      />
    );
  }

  return (
    <Layout>
      {/* Outside the routes so Cmd-K works from anywhere in the app. */}
      <CommandPalette />
      <Routes>
        <Route path="/" element={<Today />} />
        <Route path="/upcoming" element={<Upcoming />} />
        <Route path="/inbox" element={<Inbox />} />
        <Route path="/all" element={<AllTasks />} />
        <Route path="/project/:id" element={<ProjectView />} />
        <Route path="/table" element={<TableView />} />
        <Route path="/board" element={<BoardView />} />
        {/* A saved view is just a seeded table or board; the path says which. */}
        <Route path="/view/:id" element={<TableView />} />
        <Route path="/board/:id" element={<BoardView />} />
        <Route path="/search" element={<Search />} />
        <Route path="/review" element={<Review />} />
        {/* Unknown paths land on Today rather than a dead end. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}
